import React from "react";
import { Settings, Users, Calendar, Droplets, Hash } from "lucide-react";
import { useUtilitySetup } from "../../hooks";
import { InfoTooltip } from "@shared/ui/infoTooltip";
import { useNavigate } from "react-router-dom";
import { logEvent } from "@amplitude/analytics-browser";

// Card Components
const Card = ({ children, className = "", style = {}, ...props }) => (
  <div
    className={`bg-white rounded-lg shadow-sm border flex flex-col ${className}`}
    style={style}
    {...props}
  >
    {children}
  </div>
);

const CardHeader = ({ children, className = "" }) => (
  <div className={`p-4 pb-2 ${className}`}>{children}</div>
);

const CardContent = ({ children, className = "" }) => (
  <div className={`px-4 flex-grow ${className}`}>{children}</div>
);

const CardFooter = ({ children, className = "" }) => (
  <div className={`p-4 pt-2 ${className}`}>{children}</div>
);

const CardTitle = ({ children, className = "" }) => (
  <h3 className={`font-medium ${className}`}>{children}</h3>
);

const CardDescription = ({ children, className = "" }) => (
  <p className={`text-sm text-gray-600 ${className}`}>{children}</p>
);

const Badge = ({ children, variant = "default", className = "" }) => {
  const baseClasses =
    "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium";
  const variantClasses = {
    default: "bg-blue-100 text-blue-800",
    outline: "bg-gray-100 text-gray-800 border border-gray-300",
  };

  return (
    <span className={`${baseClasses} ${variantClasses[variant]} ${className}`}>
      {children}
    </span>
  );
};

const Button = ({
  children,
  variant = "default",
  className = "",
  onClick,
  ...props
}) => {
  const baseClasses = "px-4 py-2 rounded-lg font-medium transition-colors";
  const variantClasses = {
    default: "bg-blue-600 text-white hover:bg-blue-700",
    outline: "border border-gray-300 text-gray-700 hover:bg-gray-50",
  };
  return (
    <button
      className={`${baseClasses} ${variantClasses[variant]} ${className}`}
      onClick={onClick}
      {...props}
    >
      {children}
    </button>
  );
};

const SetupCardGrid = () => {
  const navigate = useNavigate();
  const handleNavigation = (url: string) => {
    console.log('debug log url', url);
    logEvent(`Utility Admin Configuration Section Opened - ${url.split('/').at(-1)}`);
    navigate(url);
  };

  const { data: setupData } = useUtilitySetup();

  // Hardcoded configuration sections
  const configSections = [
    {
      id: "core-settings",
      title: "Core System Settings",
      description:
        "Set up basic utility services, permissions, and organizational structure",
      icon: Settings,
      url: "/utility/utility-setup/core-settings",
      items: [
        {
          id: "utility-services",
          name: "Utility Services",
          description: "selecting and configuring the utility services",
        },
        {
          id: "system-access",
          name: "System Access",
          description: "configuring system access permissions",
        },
        {
          id: "categories-types",
          name: "Categories & Types",
          description: "setting up categories and types for the system",
        },
      ],
    },
    {
      id: "user-management",
      title: "Staff & Access Control",
      description: "Manage staff accounts, roles, and access permissions",
      icon: Users,
      url: "/utility/utility-setup/user-management",
      items: [
        {
          id: "job-roles",
          name: "Job Roles",
          description: "defining job roles and responsibilities",
        },
        {
          id: "access-rights",
          name: "Access Rights",
          description: "configuring access rights and permissions",
        },
        {
          id: "team-groups",
          name: "Team Groups",
          description: "setting up team groups and hierarchies",
        },
      ],
    },
    {
      id: "calendar",
      title: "Calendar & Scheduling",
      description: "Define non-working days for service and billing operations",
      icon: Calendar,
      url: "/utility/utility-setup/calendar",
      items: [
        {
          id: "holidays-closures",
          name: "Holidays & Closures",
          description: "setting up holidays and office closures",
        },
        {
          id: "annual-events",
          name: "Annual Events",
          description: "configuring annual events and schedules",
        },
      ],
    },
    {
      id: "service-area",
      title: "Service Areas",
      description: "Map out the geographic regions you service",
      icon: Droplets,
      url: "/utility/utility-setup/service-area",
      items: [
        {
          id: "coverage-zones",
          name: "Coverage Zones",
          description: "defining service coverage zones",
        },
        {
          id: "region-boundaries",
          name: "Region Boundaries",
          description: "setting up regional boundaries and limits",
        },
      ],
    },
    {
      id: "plan-tarrif",
      title: "Plans & Tariffs",
      description: "Set up rate plans and payment structures",
      icon: Settings,
      url: "/plan-tarrif/plan-tarrif-landing",
      items: [
        {
          id: "tariff",
          name: "Tariff",
          description:
            "creating and configuring utility tariffs for different services",
        },
        {
          id: "plan",
          name: "Plan",
          description: "setting up plans for different consumer categories",
        },
      ],
    },
    {
      id: "id-config",
      title: "ID & Reference Numbers",
      description:
        "Create formats for identifying customers, invoices, and service orders",
      icon: Hash,
      url: "/utility/utility-setup/id-config",
      items: [
        {
          id: "customer-id-system",
          name: "Customer ID System",
          description: "setting up customer identification system",
        },
        {
          id: "invoice-numbering",
          name: "Invoice Numbering",
          description: "configuring invoice numbering format",
        },
        {
          id: "work-order-ids",
          name: "Work Order IDs",
          description: "setting up work order identification system",
        },
      ],
    },
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
      {configSections.map((section, index) => {
        // Get API data by index position
        const apiData = setupData?.result?.[index];
        const percentage = apiData?.percentage || 0;
        const children = Array.isArray(apiData?.children)
          ? apiData.children
          : [];

        const SectionIcon = section.icon;

        return (
          <Card
            key={section.id}
            id={`section-${section.id}`}
            className="overflow-hidden transition-all duration-300 hover:shadow-md hover:-translate-y-1"
          >
            <CardHeader className="pb-2">
              <div className="flex items-start justify-between">
                <div className="flex items-center space-x-2">
                  <div className="p-1.5 bg-blue-600/10 rounded-md">
                    <SectionIcon className="h-4 w-4 text-blue-600" />
                  </div>
                  <CardTitle className="text-base font-medium">
                    {section.title}
                  </CardTitle>
                </div>
                <Badge variant={percentage === 100 ? "default" : "outline"}>
                  {`${percentage}%`}
                </Badge>
              </div>
              <div className="flex items-start mt-1">
                <CardDescription>{section.description}</CardDescription>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {section.items.map((item, itemIndex) => {
                  // Get child data by index position
                  const childData = children[itemIndex];
                  const isCompleted = childData?.completed || false;

                  return (
                    <div
                      key={item.id}
                      className="flex items-center justify-between p-2 rounded-md hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-sm">{item.name}</span>
                        <InfoTooltip content={item.description} />
                      </div>
                      {isCompleted ? (
                        <Badge
                          variant="default"
                          className="bg-blue-100 text-blue-800"
                        >
                          Configured
                        </Badge>
                      ) : (
                        <Badge variant="outline">Pending</Badge>
                      )}
                    </div>
                  );
                })}
              </div>
            </CardContent>
            <CardFooter className="pt-2">
              <Button
                className="w-full"
                onClick={() => handleNavigation(section.url)}
              >
                Configure Section
              </Button>
            </CardFooter>
          </Card>
        );
      })}
    </div>
  );
};

export default SetupCardGrid;
